// src/components/home/PackFinder.jsx
//
// The "Find your pack" wizard (home-packfinder): three questions, one recommendation.
// DETERMINISTIC -- a lookup over Site Text, no AI, no network call, nothing to
// hallucinate. It answers the most common pre-sales question ("which pack do I need?")
// instantly and at zero cost.
//
// NOTHING ABOUT PACKS, PAPERS OR PRICES IS WRITTEN HERE. Every question, option,
// outcome and label comes from Site Text via resolvePackFinder (defaults in
// src/config/siteText.js); the recommended packs render through the SAME PackCoverCard
// the home page uses, so the price / "Included in the <bundle>" / Coming Soon lines and
// the View routing are reused, not reimplemented.
//
// PRIVACY: the answers live in component state and nowhere else -- no localStorage, no
// Firestore, no analytics event. Leaving the page forgets them.
//
// LAYOUT: below `md` one question shows at a time (progress dots, Back); from `md` all
// questions sit side by side and the result updates as answers change. The collapsed
// state is a single band -- heading + button -- so the wizard does not push the rest of
// the page down until the visitor asks for it.
import React, { useEffect, useRef, useState } from 'react';
import PackCoverCard from './PackCoverCard.jsx';
import { resolvePackFinder, resolveOutcome, resolvePacks, packAction } from './packFinder.js';

const CARD = 'rounded-[var(--radius-card)] border border-book-line bg-book-page shadow-[var(--shadow-card)]';
const BTN_PRIMARY = 'px-5 py-2.5 rounded-full bg-book-accent text-book-pageDark font-display text-sm font-semibold hover:bg-book-accentLight transition-colors';
const BTN_GHOST = 'font-display text-book-accent text-sm font-semibold hover:underline';

// True when the one-question-at-a-time layout is showing (matches Tailwind's `md`).
const isSingleColumn = () => typeof window !== 'undefined' && !!window.matchMedia
    && window.matchMedia('(max-width: 767px)').matches;

function Check() {
    return (
        <svg viewBox="0 0 20 20" className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M4 10.5l4 4 8-9" />
        </svg>
    );
}

export default function PackFinder({ siteText, packs, signedIn, onNavigate, onSeeAll }) {
    const cfg = resolvePackFinder(siteText);
    const { questions } = cfg;
    const [started, setStarted] = useState(false);
    const [answers, setAnswers] = useState({});
    // Index of the question on screen in the single-column layout; === questions.length
    // means "the result". Ignored from `md` up, where everything is visible at once.
    const [step, setStep] = useState(0);
    // Where focus should land after the next render. The buttons that were just used
    // unmount or move (Start -> Restart; an answered question is replaced by the next),
    // so without this a keyboard user is dropped back on <body>.
    const pendingFocus = useRef(null);
    const headingRefs = useRef([]);
    const resultRef = useRef(null);

    useEffect(() => {
        const target = pendingFocus.current;
        if (target == null) return;
        pendingFocus.current = null;
        const el = target === 'result' ? resultRef.current : headingRefs.current[target];
        if (el && typeof el.focus === 'function') el.focus();
    });

    if (!cfg.visible) return null;

    const complete = questions.every((q) => answers[q.id]);
    const outcome = complete ? (resolveOutcome(answers, cfg.outcomes) || cfg.fallback) : null;
    const recommended = outcome ? resolvePacks(outcome.packIds, packs) : [];
    const onResult = step >= questions.length;

    const start = () => { setStarted(true); setAnswers({}); setStep(0); pendingFocus.current = 0; };

    const choose = (index, value) => {
        const next = { ...answers, [questions[index].id]: value };
        setAnswers(next);
        // Jump to the next question that still needs an answer -- so changing an early
        // answer via Back returns straight to the result instead of re-asking the rest.
        const after = questions.findIndex((q, j) => j > index && !next[q.id]);
        const any = questions.findIndex((q) => !next[q.id]);
        const nextStep = after !== -1 ? after : (any !== -1 ? any : questions.length);
        setStep(nextStep);
        // Only steal focus in the one-at-a-time layout, where the pressed button is about
        // to disappear. Side by side, focus stays on the option the visitor just chose.
        if (isSingleColumn() && nextStep !== index) pendingFocus.current = nextStep >= questions.length ? 'result' : nextStep;
    };

    const back = () => {
        const prev = Math.max(0, Math.min(step, questions.length) - 1);
        setStep(prev);
        pendingFocus.current = prev;
    };

    const labelFor = (action) => (action.kind === 'free' ? cfg.tryFreeLabel : action.kind === 'library' ? cfg.libraryLabel : cfg.seeInsideLabel);
    // Signed in, every pack's action is the same library link -- show it once, not per card.
    const libraryOnce = signedIn && recommended.length > 0;

    return (
        <section aria-labelledby="packfinder-heading" className="border-t border-book-line">
            <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 md:py-8">
                <div className={CARD + ' p-5 md:p-6'}>
                    {/* The band: all that shows until the visitor starts. */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="min-w-0">
                            <h2 id="packfinder-heading" className="font-display text-book-text text-lg md:text-xl font-bold leading-tight">{cfg.heading}</h2>
                            <p className="font-serif text-book-muted text-sm md:text-base leading-relaxed mt-1">{cfg.subheading}</p>
                        </div>
                        {!started ? (
                            <button type="button" onClick={start} className={BTN_PRIMARY + ' flex-shrink-0 self-start sm:self-auto'}>{cfg.ctaLabel}</button>
                        ) : (
                            <button type="button" onClick={start} className={BTN_GHOST + ' flex-shrink-0 self-start sm:self-auto'}>{cfg.restartLabel}</button>
                        )}
                    </div>

                    {started && (
                        <div className="mt-5 pt-5 border-t border-book-line">
                            {/* Progress + Back: only meaningful one question at a time. */}
                            <div className="md:hidden flex items-center justify-between gap-3 mb-4">
                                <div className="flex items-center gap-1.5" aria-hidden="true">
                                    {questions.map((q, i) => (
                                        <span key={q.id} className="h-2 rounded-full transition-all"
                                            style={{
                                                width: i === step ? 20 : 8,
                                                background: (answers[q.id] || i === step) ? 'rgb(var(--accent))' : 'rgb(var(--line))',
                                            }} />
                                    ))}
                                </div>
                                <span className="sr-only">
                                    {onResult ? 'All questions answered' : 'Question ' + (step + 1) + ' of ' + questions.length}
                                </span>
                                {step > 0 && (
                                    <button type="button" onClick={back} className={BTN_GHOST}>{cfg.backLabel}</button>
                                )}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                                {questions.map((q, i) => (
                                    <div key={q.id} role="group" aria-labelledby={'packfinder-q-' + q.id}
                                        className={(i === step ? 'block' : 'hidden') + ' md:block min-w-0'}>
                                        <h3 id={'packfinder-q-' + q.id} tabIndex={-1}
                                            ref={(el) => { headingRefs.current[i] = el; }}
                                            className="font-display text-book-text text-base font-bold leading-snug mb-3 focus:outline-none">
                                            {q.label}
                                        </h3>
                                        <div className="space-y-2">
                                            {q.options.map((o) => {
                                                const selected = answers[q.id] === o.value;
                                                return (
                                                    <button key={o.value} type="button" aria-pressed={selected}
                                                        onClick={() => choose(i, o.value)}
                                                        className={'w-full min-h-[48px] text-left px-4 py-3 rounded-[var(--radius-card)] border font-serif text-book-text text-sm leading-snug flex items-start gap-2 transition-colors '
                                                            + (selected ? '' : 'border-book-line hover:border-book-accent/60')}
                                                        // Selected = a tint + the accent border + a tick; the label
                                                        // stays in normal ink (accent-on-tint fails AA in this palette).
                                                        // The border is INLINE from the token on purpose: the Light skin
                                                        // remaps every .border-book-accent to the hairline colour
                                                        // (index.css, html.theme-day), which is right for decoration but
                                                        // would erase this border's job -- showing which answer is chosen.
                                                        style={selected ? { background: 'rgb(var(--accent-soft))', borderColor: 'rgb(var(--accent))' } : undefined}>
                                                        <span className="flex-1 min-w-0 [overflow-wrap:anywhere]">{o.label}</span>
                                                        {selected && <span className="text-book-text"><Check /></span>}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Always mounted once started, so the recommendation is ANNOUNCED
                                when it appears or changes rather than silently inserted. */}
                            <div aria-live="polite">
                                {outcome && (
                                    <div className={(onResult ? 'block' : 'hidden') + ' md:block md:mt-6 md:pt-6 md:border-t md:border-book-line'}>
                                        {/* Focus target after the last answer (single-column): small on
                                            purpose, so a screen reader reads the recommendation's name
                                            rather than the whole panel. */}
                                        <div ref={resultRef} tabIndex={-1} className="focus:outline-none">
                                            <div className="font-display text-book-muted text-xs font-semibold mb-1">{cfg.resultLabel}</div>
                                            {outcome.title && (
                                                <h3 className="font-display text-book-text text-xl md:text-2xl font-bold leading-tight">{outcome.title}</h3>
                                            )}
                                        </div>
                                        {outcome.why && (
                                            <p className="font-serif text-book-text text-base leading-relaxed mt-2 max-w-3xl whitespace-pre-line">{outcome.why}</p>
                                        )}

                                        {recommended.length > 0 && (
                                            <div className={'mt-5 grid grid-cols-1 gap-4 ' + (recommended.length > 1 ? 'sm:grid-cols-2 lg:grid-cols-3' : 'sm:max-w-sm')}>
                                                {recommended.map((pack) => {
                                                    const action = libraryOnce ? null : packAction(pack, packs, signedIn);
                                                    return (
                                                        <div key={pack.id} className="flex flex-col gap-3 min-w-0">
                                                            <PackCoverCard pack={pack} packs={packs} signedIn={signedIn} onNavigate={onNavigate} />
                                                            {action && (
                                                                <button type="button" onClick={() => onNavigate(action.href)} className={BTN_PRIMARY + ' self-start'}>
                                                                    {labelFor(action)}
                                                                </button>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-3">
                                            {libraryOnce && (
                                                <button type="button" onClick={() => onNavigate('/dashboard')} className={BTN_PRIMARY}>{cfg.libraryLabel}</button>
                                            )}
                                            <button type="button" onClick={onSeeAll} className={BTN_GHOST}>{cfg.seeAllLabel}</button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}
